import {
	type LodManifest,
	MAX_LOD_CHUNK_BYTES,
	MAX_LOD_MANIFEST_BYTES,
	parseLodManifest,
} from "@repo/api/src/schemas/lod";
import { useEffect, useRef, useState } from "react";
import { trpc } from "../../../lib/trpc";
import { useChunkedUpload } from "../../hooks/useChunkedUpload";
import { useTranslation } from "../../i18n";
import { startLodGeneration } from "../../utils/generate-lod.client";
import type { GenerationProgress } from "../../utils/lod-generation";
import { DEFAULT_LOD_LEVEL_RATIOS, validateLodGenerationOptions } from "../../utils/lod-generation-options";
import { lodManifestRatios, suggestedLodDistances, validateLodDistanceInputs } from "../../utils/lod-settings";
import { parseDescriptionWithMetadata } from "../../utils/splatMetadata";
import { GaussianViewer } from "../GaussianViewer";
import { Alert, Button, Modal } from "../ui";
import { LodLevelSettings } from "./LodLevelSettings";
import { formatBytes } from "./format";
import type { UploadRow } from "./types";

type Draft = { manifest: LodManifest; files: File[]; urls: string[]; src: string; bytes: number; settings?: string };
type Method = "automatic" | "files";
type Operation = "generate" | "upload" | "activate";

function generationStatus(progress: GenerationProgress, ja: boolean) {
	const names = ja
		? {
				download: "元モデルを取得中",
				read: "3Dデータを確認中",
				decimate: progress.phase === "write-level" ? "点数を減らしたデータを保存中" : "点数を減らしたデータを作成中",
				compress:
					progress.phase === "partition"
						? "配信データを分割中"
						: progress.phase === "textures"
							? "画像データを圧縮中"
							: "配信データを圧縮中",
				package: "配信ファイルを作成中",
			}
		: {
				download: "Downloading original",
				read: "Reading splats",
				decimate: progress.phase === "write-level" ? "Saving reduced detail" : "Creating reduced detail",
				compress:
					progress.phase === "partition"
						? "Partitioning delivery data"
						: progress.phase === "textures"
							? "Compressing textures"
							: "Compressing delivery data",
				package: "Packaging delivery files",
			};
	// Library percentages describe repeated internal passes. Only the download
	// has a byte-based percentage; encoding uses actual output file units.
	const detail =
		progress.stage === "download" && progress.percent !== undefined
			? ` · ${progress.percent}%`
			: progress.stage === "decimate" && progress.level && progress.levelTotal
				? ` · LOD ${progress.level} (${progress.level}/${progress.levelTotal})`
				: progress.stage === "compress" && progress.unit && progress.unitTotal
					? ` · ${ja ? "ファイル" : "File"} ${progress.unit}/${progress.unitTotal}`
					: "";
	return names[progress.stage] + detail;
}
export function LodDeliveryDialog({
	upload,
	onClose,
	onSaved,
	autoStart = false,
}: { upload: UploadRow; onClose: () => void; onSaved: (updated: UploadRow) => void; autoStart?: boolean }) {
	const ja = useTranslation().locale === "ja";
	const utils = trpc.useUtils();
	const editing = trpc.library.getForEditing.useQuery({ id: upload.id }, { retry: false, refetchOnWindowFocus: false });
	const commit = trpc.library.commitLodDelivery.useMutation({ networkMode: "always" });
	const remove = trpc.library.removeLodDelivery.useMutation({ networkMode: "always" });
	const saveDistances = trpc.library.saveLodDistances.useMutation({ networkMode: "always" });
	const uploader = useChunkedUpload();
	const [draft, setDraft] = useState<Draft | null>(null);
	const [ready, setReady] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [stage, setStage] = useState("");
	const [generating, setGenerating] = useState(false);
	const [method, setMethod] = useState<Method>("automatic");
	const [operation, setOperation] = useState<Operation | null>(null);
	const [progress, setProgress] = useState<GenerationProgress | null>(null);
	const [elapsed, setElapsed] = useState(0);
	const [distances, setDistances] = useState<string[] | null>(null);
	const [previewDistances, setPreviewDistances] = useState<number[] | undefined>();
	const [initialPercentages, setInitialPercentages] = useState<string | null>(null);
	const [pointsKnown, setPointsKnown] = useState(false);
	const [levelPercentages, setLevelPercentages] = useState(() =>
		DEFAULT_LOD_LEVEL_RATIOS.map(ratio => String(ratio * 100)),
	);
	const startedAt = useRef(0);
	const busyRef = useRef(false);
	const selection = useRef(0);
	const generation = useRef<ReturnType<typeof startLodGeneration> | null>(null);
	const autoStarted = useRef(false);
	const draftRef = useRef(draft);
	const previewSourceRef = useRef<string | undefined>(undefined);
	const previewFailedRef = useRef(false);
	draftRef.current = draft;
	useEffect(
		() => () => {
			selection.current++;
			generation.current?.cancel();
			for (const url of draftRef.current?.urls ?? []) URL.revokeObjectURL(url);
		},
		[],
	);
	useEffect(() => {
		if (!busy) return;
		const prevent = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", prevent);
		return () => window.removeEventListener("beforeunload", prevent);
	}, [busy]);
	useEffect(() => {
		if (!busy) return;
		const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000)), 1000);
		return () => window.clearInterval(timer);
	}, [busy]);
	const metadata = parseDescriptionWithMetadata(editing.data?.description).metadata;
	const lod = metadata?.lod;
	const generationOptions = { levelRatios: levelPercentages.map(value => Number(value) / 100) };
	const settingsKey = JSON.stringify(levelPercentages);
	const pointsChanged = initialPercentages !== null && settingsKey !== initialPercentages;
	const draftStale = !!draft?.settings && draft.settings !== settingsKey;
	const needsGeneration = method === "automatic" && (draft ? draftStale : !lod || pointsChanged);
	const distanceError = validateLodDistanceInputs(distances, levelPercentages.length + 1, ja);
	const previewSrc = draft?.src || (lod && editing.data?.streamed ? editing.data.viewerUrl : undefined);
	const previewLevels = draft?.manifest.lodLevels ?? lod?.levels;
	const canSave = !needsGeneration && !distanceError && (draft ? ready : method === "automatic" && !!lod);
	previewSourceRef.current = previewSrc;
	useEffect(() => {
		if (!distanceError && (distances === null || distances.length + 1 === previewLevels))
			setPreviewDistances(distances?.map(Number));
	}, [distances, distanceError, previewLevels]);
	useEffect(() => {
		if (!editing.data) return;
		let active = true;
		const current = parseDescriptionWithMetadata(editing.data.description).metadata?.lod;
		setDistances(current?.distances?.map(String) ?? null);
		const ratios =
			current?.levelRatios ?? Array.from({ length: (current?.levels ?? 4) - 1 }, (_, index) => 0.5 ** (index + 1));
		const percentages =
			current && !current.levelRatios
				? (Array(current.levels - 1).fill("") as string[])
				: ratios.map(ratio => String(Number((ratio * 100).toPrecision(8))));
		setLevelPercentages(percentages);
		setInitialPercentages(JSON.stringify(percentages));
		setPointsKnown(!current || !!current.levelRatios);
		if (current && !current.levelRatios && editing.data.streamed) {
			void fetch(editing.data.viewerUrl)
				.then(async response => {
					if (!response.ok) throw new Error("Preview manifest unavailable");
					return parseLodManifest(await response.json());
				})
				.then(manifest => {
					const actual = lodManifestRatios(manifest);
					if (!active || !actual) return;
					const values = actual.map(ratio => String(Number((ratio * 100).toPrecision(8))));
					if (!draftRef.current) setLevelPercentages(values);
					setInitialPercentages(JSON.stringify(values));
					setPointsKnown(true);
				})
				.catch(() => {
					/* Distance settings remain available if count lookup fails. */
				});
		}
		return () => {
			active = false;
		};
	}, [editing.data?.revision]);
	const changePercentages = (values: string[]) => {
		setLevelPercentages(values);
		if (distances !== null) {
			setDistances(
				values.map((_, index) => distances[index] ?? String(Math.min(1_000_000, Number(distances.at(-1)) * 3))),
			);
		}
	};
	const changeDistance = (index: number, value: string) => {
		setDistances(
			(distances ?? suggestedLodDistances(levelPercentages.length)).map((current, position) =>
				position === index ? value : current,
			),
		);
	};
	const levelValidation = validateLodGenerationOptions(generationOptions);
	const levelError = levelValidation
		? ja
			? levelValidation
			: "Use 1–7 levels with percentages greater than 0 and less than 100. Each level must have fewer splats than the previous level."
		: null;
	const finish = async (updated: UploadRow) => {
		// Activation has succeeded. A failed background refresh must not turn it
		// into a retryable failure and lead to a second activation.
		void Promise.allSettled([
			utils.library.listMine.invalidate(),
			utils.library.summary.invalidate(),
			utils.library.getForEditing.invalidate({ id: upload.id }),
			utils.viewer.getGaussianSplat.invalidate({ shareId: upload.shareId }),
		]);
		onSaved(updated);
	};
	const clearDraft = () => {
		selection.current++;
		for (const url of draftRef.current?.urls ?? []) URL.revokeObjectURL(url);
		draftRef.current = null;
		setDraft(null);
		setReady(false);
		previewFailedRef.current = false;
	};
	const select = async (files: File[], preview = true): Promise<Draft | null> => {
		setError(null);
		clearDraft();
		const selected = selection.current;
		const urls: string[] = [];
		try {
			const manifestFile = files.find(file => file.name === "lod-meta.json");
			if (!manifestFile || manifestFile.size > MAX_LOD_MANIFEST_BYTES)
				throw new Error(
					ja
						? "lod-meta.json と同じフォルダーの .sog ファイルを一緒に選んでください。"
						: "Choose lod-meta.json and its .sog files together.",
				);
			const manifest = parseLodManifest(JSON.parse(await manifestFile.text()));
			if (selected !== selection.current) return null;
			const chunks = manifest.filenames.map(name => {
				const matches = files.filter(file => file.name === name);
				if (matches.length !== 1 || matches[0].size <= 0 || matches[0].size > MAX_LOD_CHUNK_BYTES)
					throw new Error(
						`${name}: ${ja ? "ファイルが不足・重複しています。1ファイル128 MBまでです。" : "Missing, duplicated or larger than 128 MB."}`,
					);
				return matches[0];
			});
			const bytes = chunks.reduce((sum, file) => sum + file.size, 0) + manifestFile.size;
			if (bytes + (lod?.sourceBytes ?? editing.data?.fileSizeBytes ?? 0) > 2_000_000_000)
				throw new Error(
					ja
						? "元モデルと配信データの合計を2 GB以内にしてください。"
						: "Original model and delivery data must fit within 2 GB.",
				);
			let src = "";
			if (preview) {
				for (const file of chunks) urls.push(URL.createObjectURL(file));
				const blob = new Blob([JSON.stringify({ ...manifest, filenames: urls.map(url => `${url}#chunk.sog`) })], {
					type: "application/json",
				});
				const manifestUrl = URL.createObjectURL(blob);
				urls.push(manifestUrl);
				src = `${manifestUrl}#lod-meta.json`;
			}
			const next = { manifest, files: chunks, urls, bytes, src };
			if (preview) {
				draftRef.current = next;
				setDraft(next);
			}
			return next;
		} catch (cause) {
			for (const url of urls) URL.revokeObjectURL(url);
			if (selected === selection.current) setError(cause instanceof Error ? cause.message : String(cause));
			return null;
		}
	};
	const uploadDraft = async (next: Draft, automatic = false) => {
		if (!editing.data) throw new Error("The owned model could not be loaded.");
		const bundleId = crypto.randomUUID();
		const files = [];
		setOperation("upload");
		for (const [index, file] of next.files.entries()) {
			setStage(`${ja ? "ファイル" : "File"} ${index + 1}/${next.files.length}`);
			const result = await uploader.uploadFile(file, undefined, { id: upload.id, kind: "lod", bundleId });
			if (!result.success || !result.fileUrl) throw new Error(result.error || "Upload failed");
			files.push({ name: file.name, url: result.fileUrl });
		}
		setStage(ja ? "配信を切り替え中…" : "Activating delivery…");
		setOperation("activate");
		await finish(
			await commit.mutateAsync({
				id: upload.id,
				revision: editing.data.revision,
				bundleId,
				manifest: JSON.stringify(next.manifest),
				files,
				levelRatios: next.settings
					? (JSON.parse(next.settings) as string[]).map(value => Number(value) / 100)
					: lodManifestRatios(next.manifest),
				distances: !automatic && distances ? distances.map(Number) : undefined,
			}),
		);
	};
	const generate = async (publish = false) => {
		if (
			!editing.data ||
			busyRef.current ||
			editing.isFetching ||
			(!publish && (levelValidation || distanceError || !pointsKnown))
		)
			return;
		busyRef.current = true;
		startedAt.current = Date.now();
		setElapsed(0);
		setBusy(true);
		setGenerating(true);
		setOperation("generate");
		setProgress(null);
		setStage(ja ? "自動生成を開始中" : "Starting generation");
		setError(null);
		clearDraft();
		try {
			const job = startLodGeneration(
				editing.data.editorUrl,
				`model.${editing.data.modelType}`,
				progress => {
					setProgress(progress);
					setStage(generationStatus(progress, ja));
				},
				publish ? undefined : generationOptions,
			);
			generation.current = job;
			const delivery = await job.result;
			generation.current = null;
			setGenerating(false);
			const next = await select(
				[
					new File([delivery.manifest], "lod-meta.json"),
					...delivery.files.map(file => new File([file.blob], file.name)),
				],
				!publish,
			);
			if (!next)
				throw new Error(
					ja
						? "生成した配信データを検証できませんでした。元モデルの配信は維持されています。"
						: "Generated delivery could not be validated. The original remains active.",
				);
			if (publish) await uploadDraft(next, true);
			else {
				next.settings = settingsKey;
				setDraft({ ...next });
				setStage(ja ? "プレビューを確認して保存してください。" : "Check the preview, then save.");
			}
		} catch (cause) {
			if (cause instanceof Error && cause.name === "AbortError")
				setStage(ja ? "生成を中止しました。元の配信は変わりません。" : "Generation cancelled. Delivery is unchanged.");
			else {
				setStage("");
				setError(
					`${ja ? "自動生成に失敗しました。元の配信は維持されています。 " : "Generation failed. Previous delivery remains active. "}${cause instanceof Error ? cause.message : String(cause)}`,
				);
			}
		} finally {
			generation.current = null;
			busyRef.current = false;
			setBusy(false);
			setGenerating(false);
			setOperation(null);
		}
	};
	const generateRef = useRef(generate);
	generateRef.current = generate;
	useEffect(() => {
		if (autoStart && editing.data && !editing.isFetching && !autoStarted.current) {
			autoStarted.current = true;
			void generateRef.current(true);
		}
	}, [autoStart, editing.data, editing.isFetching]);
	const save = async () => {
		if (!editing.data || busyRef.current || editing.isFetching || !canSave) return;
		busyRef.current = true;
		startedAt.current = Date.now();
		setElapsed(0);
		setBusy(true);
		setError(null);
		try {
			if (draft) await uploadDraft(draft);
			else {
				setStage(ja ? "設定を保存中…" : "Saving settings…");
				await finish(
					await saveDistances.mutateAsync({
						id: upload.id,
						revision: editing.data.revision,
						distances: distances?.map(Number) ?? null,
					}),
				);
			}
		} catch (cause) {
			setStage("");
			setError(cause instanceof Error ? cause.message : String(cause));
		} finally {
			busyRef.current = false;
			setBusy(false);
			setOperation(null);
		}
	};
	const changeMethod = (value: Method) => {
		if (busyRef.current || value === method) return;
		clearDraft();
		setError(null);
		setStage("");
		setProgress(null);
		setMethod(value);
		// Restore published settings when abandoning an imported draft.
		if (value === "automatic" && initialPercentages) {
			setLevelPercentages(JSON.parse(initialPercentages) as string[]);
		}
		setDistances(lod?.distances?.map(String) ?? null);
	};
	const steps =
		method === "automatic"
			? [ja ? "LODデータを作成" : "Generate LOD", ja ? "アップロード" : "Upload", ja ? "配信切替" : "Activate"]
			: [ja ? "アップロード" : "Upload", ja ? "配信切替" : "Activate"];
	const activeStep = operation === "generate" ? 0 : operation === "upload" ? steps.length - 2 : steps.length - 1;
	const uploadStatus =
		uploader.progress?.stage === "finalizing"
			? ja
				? "配信データを保存中"
				: "Saving delivery data"
			: uploader.progress?.stage === "waiting" || uploader.progress?.stage === "link"
				? ja
					? "サーバーの応答を待っています"
					: "Waiting for the server"
				: ja
					? "配信データをアップロード中"
					: "Uploading delivery data";
	return (
		<Modal
			className="account-surface account-dialog account-lod-dialog overflow-x-hidden"
			open
			onOpenChange={open => {
				if (!open && !busy) onClose();
			}}
			title={ja ? "LOD配信を設定" : "LOD delivery settings"}
			closeLabel={ja ? "閉じる" : "Close"}
			busy={busy}
			description={
				ja
					? "点数と切り替え距離を、プレビューを見ながら設定します。"
					: "Adjust splat counts and switch distances with a live preview."
			}
			footer={
				<>
					<Button variant="secondary" disabled={busy} onClick={onClose}>
						{ja ? "閉じる" : "Close"}
					</Button>
					{!autoStart && (
						<Button disabled={busy || editing.isFetching || !editing.data || !canSave} onClick={() => void save()}>
							{ja ? "保存" : "Save"}
						</Button>
					)}
				</>
			}
		>
			<div className="account-dialog-body min-w-0">
				{autoStart && !busy && (
					<Button disabled={editing.isFetching || !editing.data} onClick={() => void generate(true)}>
						{ja ? "LODを自動生成して配信する" : "Automatically optimize this model"}
					</Button>
				)}
				<div className="account-preview-heading">
					<p>{upload.title || upload.shareId}</p>
					{editing.data && <span>{formatBytes(lod?.sourceBytes ?? editing.data.fileSizeBytes)}</span>}
				</div>
				{!busy && !autoStart && (
					<>
						<fieldset className="account-lod-source" aria-label={ja ? "LODデータ" : "LOD data"}>
							<Button
								variant={method === "automatic" ? "secondary" : "ghost"}
								size="sm"
								aria-pressed={method === "automatic"}
								onClick={() => changeMethod("automatic")}
							>
								{ja ? "モデルから作成" : "From model"}
							</Button>
							<Button
								variant={method === "files" ? "secondary" : "ghost"}
								size="sm"
								aria-pressed={method === "files"}
								onClick={() => changeMethod("files")}
							>
								{ja ? "ファイルを使う" : "Use files"}
							</Button>
						</fieldset>
						{method === "files" && (
							<section className="account-dialog-section">
								<label className="account-file-field">
									{ja ? "LODのファイルをまとめて選択" : "Choose the LOD files together"}
									<input
										type="file"
										multiple
										accept=".json,.sog"
										className="account-file-input"
										disabled={editing.isFetching || !editing.data}
										onChange={event => {
											void select(Array.from(event.target.files ?? [])).then(next => {
												if (!next) return;
												if (draftRef.current !== next) return;
												const ratios = lodManifestRatios(next.manifest);
												setLevelPercentages(
													ratios?.map(ratio => String(Number((ratio * 100).toPrecision(8)))) ??
														Array(next.manifest.lodLevels - 1).fill(""),
												);
												setDistances(null);
											});
											event.target.value = "";
										}}
									/>
								</label>
								<p className="account-dialog-caption">
									{ja
										? "lod-meta.json と全 .sog ファイルを選択します。元モデルと同じ向き・座標・縮尺で書き出してください。"
										: "Select lod-meta.json and every referenced .sog file, using the original orientation, coordinates and scale."}
								</p>
							</section>
						)}
						<div className="account-lod-settings-layout">
							<div className="account-dialog-section">
								<LodLevelSettings
									ja={ja}
									percentages={levelPercentages}
									distances={distances}
									levelError={needsGeneration ? levelError : null}
									distanceError={distanceError}
									editablePoints={method === "automatic" && pointsKnown}
									onPercentagesChange={changePercentages}
									onDistanceChange={changeDistance}
									onResetDistances={() => setDistances(null)}
								/>
								{method === "automatic" && needsGeneration && (
									<Button
										disabled={
											editing.isFetching || !editing.data || !pointsKnown || !!levelValidation || !!distanceError
										}
										onClick={() => void generate()}
									>
										{ja
											? lod || draft
												? "LODを作り直してプレビュー"
												: "LODを作成してプレビュー"
											: "Generate LOD preview"}
									</Button>
								)}
							</div>
							<section className="account-dialog-section" aria-label={ja ? "配信プレビュー" : "Delivery preview"}>
								<div className="account-preview-heading">
									<h3>{ja ? "プレビュー" : "Preview"}</h3>
									<span>
										{draft ? (ja ? "保存前" : "Unsaved") : lod ? (ja ? "現在の配信" : "Current delivery") : ""}
									</span>
								</div>
								<div className="account-lod-preview">
									{previewSrc ? (
										<GaussianViewer
											format="ply"
											src={previewSrc}
											previewMode
											initialCameraPose={metadata?.camera}
											centerMode={metadata?.centerMode}
											collision={metadata?.collision}
											lodDistances={previewDistances}
											onAssetProgress={progress => {
												if (previewSourceRef.current === previewSrc)
													setReady(progress >= 1 && !previewFailedRef.current);
											}}
											onError={failure => {
												if (previewSourceRef.current !== previewSrc) return;
												previewFailedRef.current = true;
												setReady(false);
												setError(failure.message);
											}}
										/>
									) : (
										<div className="account-preview-loading">
											{lod && !editing.data?.streamed
												? ja
													? "公開期限を延長すると、現在のLODをプレビューできます。"
													: "Extend the sharing expiry to preview the current LOD."
												: ja
													? "LODを作成すると、ここで確認できます。"
													: "Generate LOD data to see the preview here."}
										</div>
									)}
								</div>
								<p className="account-dialog-caption">
									{ja
										? "ドラッグで回転、スクロールで拡大・縮小。距離の変更はすぐに反映されます。描画上限に達すると、点数が少ないLODを表示する場合があります。"
										: "Drag to orbit and scroll to move. Distance changes appear immediately. Rendering limits may select a lower-detail LOD."}
								</p>
								{draftStale && (
									<p className="account-dialog-caption">
										{ja
											? "点数が変更されています。LODを作り直すとプレビューに反映されます。"
											: "Regenerate the preview to apply changed splat counts."}
									</p>
								)}
							</section>
						</div>
					</>
				)}
				{busy && operation && (
					<ol className="account-lod-steps" aria-label={ja ? "配信切替までの手順" : "Delivery steps"}>
						{steps.map((step, index) => (
							<li
								key={step}
								aria-current={index === activeStep ? "step" : undefined}
								data-complete={index < activeStep || undefined}
							>
								<span aria-hidden="true">{index < activeStep ? "✓" : index + 1}</span>
								{step}
							</li>
						))}
					</ol>
				)}
				{stage && (
					<div className="account-progress account-lod-progress" data-active={busy || undefined}>
						<div className="account-dialog-section">
							<output aria-live="polite">
								<span className="account-progress-dot" aria-hidden="true" />
								{operation === "upload"
									? `${uploadStatus} · ${stage}${uploader.progress?.stage === "sending" ? ` · ${uploader.progress.percentage}%` : ""}`
									: stage}
							</output>
							{busy && (
								<p className="account-dialog-caption">
									{ja
										? `経過 ${Math.floor(elapsed / 60)}分${elapsed % 60}秒`
										: `Elapsed ${Math.floor(elapsed / 60)}m ${elapsed % 60}s`}
								</p>
							)}
							{generating && progress?.stage === "compress" && (
								<p className="account-dialog-caption">
									{ja
										? "点数が多いモデルでは、圧縮に時間がかかる場合があります。"
										: "Models with many splats can take longer to compress."}
								</p>
							)}
						</div>
						{generating && (
							<Button variant="ghost" size="sm" onClick={() => generation.current?.cancel()}>
								{ja ? "生成を中止" : "Cancel generation"}
							</Button>
						)}
					</div>
				)}

				{lod && !busy && (
					<section className="account-delivery-state" aria-label={ja ? "現在の配信" : "Current delivery"}>
						<p>
							{ja ? "LOD配信中" : "LOD delivery enabled"} · {lod.levels} LOD · {formatBytes(lod.bytes)}
						</p>
						<Button
							variant="ghost"
							size="sm"
							disabled={busy || editing.isFetching}
							onClick={async () => {
								if (!editing.data || busyRef.current) return;
								busyRef.current = true;
								startedAt.current = Date.now();
								setElapsed(0);
								setBusy(true);
								setStage(ja ? "元モデルの配信に戻しています" : "Restoring original delivery");
								setError(null);
								try {
									await finish(await remove.mutateAsync({ id: upload.id, revision: editing.data.revision }));
								} catch (cause) {
									setStage("");
									setError(String(cause));
								} finally {
									busyRef.current = false;
									setBusy(false);
								}
							}}
						>
							{ja ? "元モデルの配信に戻す" : "Serve the original model"}
						</Button>
					</section>
				)}
				{(error || editing.error) && <Alert tone="error">{error || editing.error?.message}</Alert>}
				<p className="account-dialog-caption">
					{ja
						? "元モデルと共有URLは変わりません。元モデルを編集して保存した場合は、LODを作り直してください。"
						: "The original model and share URL are kept. Regenerate LOD data after saving model edits."}
				</p>
			</div>
		</Modal>
	);
}
